<script setup>
import { ref, onMounted } from 'vue'
import { systemMonitorgetCert, systemMonitorgetHealth, systemMonitorgetHealthCheck } from '@/api/cfes/systemMonitoring'
import { queueMonitorGetSqsMetrics } from '@/api/cfes/queueMonitoring'

const loading = ref(true)
const status = ref(null)
const serviceMetaInfo = ref([])
const serviceMetaInfoHeaders = [
  { title: 'Application Id', key: 'applicationID' },
  { title: 'Application Name', key: 'applicationName' },
  { title: 'Version', key: 'version' },
  { title: 'Build Date', key: 'buildDate' },
  { title: 'Host', key: 'host' },
  { title: 'Timestamp', key: 'timestamp' },
]
const webpageMetaInfo = ref([])
const webpageMetaInfoHeaders = [
  { title: 'Version', key: 'version' },
  { title: 'CommitId', key: 'commitId' },
  { title: 'Build Date', key: 'buildDate' },
]
const diskspace = ref([])
const diskSpaceHeaders = [
  { title: 'Status', key: 'status' },
  { title: 'Total(GB)', key: 'total' },
  { title: 'Free(GB)', key: 'free' },
  { title: 'Details', key: 'details' },
]
const components = ref([])
const healthCheckComponentHeaders = [
  { title: 'Title', key: 'title' },
  { title: 'Component', key: 'component' },
  { title: 'Result', key: 'result' },
  { title: 'Time', key: 'time' },
  { title: 'Error', key: 'error' },
  { title: 'Description', key: 'description' },
]
const envInfo = ref([])
const envInfoHeaders = [
  { title: 'Server', key: 'tomcat' },
  { title: 'Docker', key: 'docker' },
  { title: 'Java', key: 'java' },
]
const sqsMetrics = ref([])
const sqsMetricsHeaders = [
  { title: 'Queue Name', key: 'queueName' },
  { title: 'Available', align: 'center', key: 'approximateNumberOfMessages' },
  { title: 'In-Flight', align: 'center', key: 'approximateNumberOfMessagesNotVisible' },
  { title: 'Delayed', align: 'center', key: 'approximateNumberOfMessagesDelayed' },
  { title: 'Status', align: 'center', key: 'status', sortable: false },
]
const appConfig = ref([])
const appConfigHeaders = [
  { title: 'Config Name', key: 'configName' },
  { title: 'Config Value', key: 'configValue' },
]
const certCheck = ref([])
const certCheckHeaders = [
  { title: 'Title', key: 'title' },
  { title: 'Activation Date', key: 'activationDate' },
  { title: 'Expiration Date', key: 'expirationDate' },
]

const parseByteToGigaByte = (bytes) => (bytes / 1024 ** 3).toFixed(1)
const normalizeSqsMetrics = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.results)) return response.results
  return []
}

const prepareComponentCheckResult = (componentsData) => {
  const list = []
  const pushItem = (title, comp, statusVal, time, msg, desc) =>
    list.push({ title, component: comp, result: statusVal, time, error: msg, description: desc })

  if (componentsData.db) {
    pushItem('DataBase', 'PostgreSql', componentsData.db.status, '-', '-', 'DB access check')
  }

  const mapExecution = (key) => {
    const item = componentsData[key]
    if (!item || !item.details?.EXECUTION_SUMMARY?.results?.length) return
    const summary = item.details.EXECUTION_SUMMARY
    const first = summary.results[0]
    pushItem(first.title, first.component, item.status, `${summary.duration} ms`, first.message, first.description)
  }

  ['fodInterfaceHealthCheck', 'msgHealthCheck', 'oauthHealthCheck', 'webSocketHealthCheck', 'opensearchHealthCheck'].forEach(mapExecution)
  const sqsHealthKey = ['sqsCheck', 'sqsHealthCheck'].find((key) => componentsData[key])
  if (sqsHealthKey) mapExecution(sqsHealthKey)

  components.value = list
}

const prepareAppConfig = (applicationConfiguration = []) => {
  appConfig.value = applicationConfiguration.map((c) => ({ configName: c[0], configValue: c[1] }))
}

const parseWebpageInfo = (metaFallback = {}) => {
  const safeVal = (v, fb) => v || fb || 'N/A'
  const info = {
    version: safeVal(import.meta.env.VITE_APP_VERSION, metaFallback.version),
    commitId: safeVal(import.meta.env.VITE_APP_COMMIT_ID, metaFallback.commitId),
    buildDate: safeVal(import.meta.env.VITE_APP_BUILD_DATE, metaFallback.buildDate),
  }
  webpageMetaInfo.value = [info]
}

const fetchData = async () => {
  loading.value = true
  components.value = []
  sqsMetrics.value = []
  appConfig.value = []
  envInfo.value = []
  serviceMetaInfo.value = []
  diskspace.value = []
  certCheck.value = []

  try {
    const [health, cert, healthCheck, sqsResult] = await Promise.all([
      systemMonitorgetHealth(),
      systemMonitorgetCert(),
      systemMonitorgetHealthCheck(),
      queueMonitorGetSqsMetrics().catch((error) => {
        console.error('Failed to load SQS metrics', error)
        return []
      }),
    ])

    status.value = health.status
    if (health.components?.diskSpace) {
      const ds = health.components.diskSpace
      diskspace.value = [
        {
          status: ds.status,
          details: JSON.stringify(ds.details, null, '\t'),
          total: parseByteToGigaByte(ds.details.total),
          free: parseByteToGigaByte(ds.details.free),
        },
      ]
    }
    prepareComponentCheckResult(health.components || {})

    certCheck.value = cert || []
    sqsMetrics.value = normalizeSqsMetrics(sqsResult)

    if (healthCheck) {
      if (healthCheck.MetaInfo) serviceMetaInfo.value = [healthCheck.MetaInfo]
      if (healthCheck.envInfo) envInfo.value = [healthCheck.envInfo]
      prepareAppConfig(healthCheck.ApplicationConfiguration)
      parseWebpageInfo(healthCheck.MetaInfo || {})
    }
  } catch (err) {
    console.error('Failed to load system monitoring data', err)
  } finally {
    if (!webpageMetaInfo.value.length) parseWebpageInfo()
    loading.value = false
  }
}

onMounted(fetchData)
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">System Monitoring</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <template v-if="loading">
      <div class="loader-container">
        <v-progress-circular indeterminate :size="120" :width="8" color="primary">
          loading
        </v-progress-circular>
      </div>
    </template>

    <template v-else>
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card class="section-card">
            <v-card-title class="section-title d-flex align-center justify-space-between">
              <span>Service Status</span>
              <v-chip color="success" variant="flat" size="small">{{ status }}</v-chip>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="section-grid">
        <v-col cols="12">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">Backend Service Information</v-card-title>
            <v-data-table
              density="compact"
              :items="serviceMetaInfo"
              :headers="serviceMetaInfoHeaders"
              disable-sort
              disable-pagination
              hide-default-footer
              class="elevation-2 monitoring-table"
            />
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">Webpage Information</v-card-title>
            <v-data-table
              density="compact"
              :items="webpageMetaInfo"
              :headers="webpageMetaInfoHeaders"
              disable-sort
              disable-pagination
              hide-default-footer
              class="elevation-2 monitoring-table"
            />
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">Environment Information</v-card-title>
            <v-data-table
              density="compact"
              :items="envInfo"
              :headers="envInfoHeaders"
              disable-sort
              disable-pagination
              hide-default-footer
              class="elevation-2 monitoring-table"
            />
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">Disk Resource Status</v-card-title>
            <v-data-table
              density="compact"
              :items="diskspace"
              :headers="diskSpaceHeaders"
              disable-sort
              disable-pagination
              hide-default-footer
              class="elevation-2 monitoring-table"
            >
              <template #item.details="{ item }">
                <pre class="code-block">{{ item.details }}</pre>
              </template>
            </v-data-table>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">Components Status</v-card-title>
            <v-data-table
              density="compact"
              :items="components"
              :headers="healthCheckComponentHeaders"
              disable-sort
              disable-pagination
              hide-default-footer
              class="elevation-2 monitoring-table"
            />
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">SQS Metrics</v-card-title>
            <v-data-table
              density="compact"
              :items="sqsMetrics"
              :headers="sqsMetricsHeaders"
              :items-per-page="sqsMetrics.length || 10"
              hide-default-footer
              no-data-text="No SQS metrics available"
              class="elevation-2 monitoring-table"
            >
              <template #item.approximateNumberOfMessages="{ item }">
                {{ item.approximateNumberOfMessages ?? '-' }}
              </template>
              <template #item.approximateNumberOfMessagesNotVisible="{ item }">
                {{ item.approximateNumberOfMessagesNotVisible ?? '-' }}
              </template>
              <template #item.approximateNumberOfMessagesDelayed="{ item }">
                {{ item.approximateNumberOfMessagesDelayed ?? '-' }}
              </template>
              <template #item.status="{ item }">
                <v-chip
                  :color="item.error ? 'error' : 'success'"
                  size="small"
                  variant="flat"
                  :title="item.error || 'SQS metrics loaded successfully'"
                >
                  {{ item.error ? 'Error' : 'OK' }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">Application Configuration</v-card-title>
            <v-data-table
              density="compact"
              :items="appConfig"
              :headers="appConfigHeaders"
              disable-sort
              disable-pagination
              hide-default-footer
              class="elevation-2 monitoring-table"
            />
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="section-card h-100">
            <v-card-title class="section-title">Certificate Check</v-card-title>
            <v-data-table
              density="compact"
              :items="certCheck"
              :headers="certCheckHeaders"
              disable-sort
              disable-pagination
              hide-default-footer
              class="elevation-2 monitoring-table"
            />
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  width: 100%;
  min-width: 0;
  padding: 6px 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
}

.section-title {
  padding: 6px 4px;
  font-weight: 700;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
}

.code-block {
  max-width: 100%;
  margin: 0;
  background: #0f172a;
  color: #e2e8f0;
  padding: 8px;
  border-radius: 6px;
  font-size: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.section-grid > .v-col {
  display: flex;
}

.monitoring-table :deep(.v-table__wrapper) {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow-x: auto;
}

.monitoring-table :deep(thead th) {
  background-color: #f1f5f9;
  color: #111827;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}

@media (max-width: 600px) {
  .page-shell {
    padding: 8px 4px 20px;
  }
}
</style>

